import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import CourierServiceList from "./CourierServiceList";
import EmployeeAuthModal from "../../employeeAuth/EmployeeAuthModal";
import CustomDropdown from "./Dropdown"
import { Notification } from "../../Notification"
import Cookies from "js-cookie"
import { FaEdit, FaPlus } from "react-icons/fa";
import { getUserInfoFromToken } from "../../utils/session";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateNewCourier({ isSidebarAdmin }) {
  const [showEmployeeAuthModal, setShowEmployeeAuthModal] = useState(false);
  const [employeeAccess, setEmployeeAccess] = useState({ canView: false, canAction: false, isAdmin: false });
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    _id: null,
    id: null,
    provider: "",
    courier: "",
    courierType: "",
    name: "",
    status: "",
    courier_id: "",
  });

  const [courierProviders, setCourierProviders] = useState([]);
  const [providerServices, setProviderServices] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);

  // The Provider dropdown lists courier account NAMES (an admin may call the Jiffy account "Jiffy Main"), so
  // what decides which input to show is the account's real provider type, not the name.
  const providerTypeOf = (name) =>
    courierProviders.find((c) => c.courierName === name)?.courierProvider || name;

  // accountName: for Shiprocket, which of the company's Shiprocket accounts to list couriers from
  const fetchServicesForProvider = async (providerName, accountName) => {
    setProviderServices([]);
    setServicesLoading(true);
    try {
      let services = [];
      switch (providerName) {
        case "NimbusPost":
          const nimbusRes = await axios.get(`${REACT_APP_BACKEND_URL}/NimbusPost/getCourierServices`);
          // Keep NimbusPost's courier id: without it NimbusPost picks any courier it likes at booking
          services = nimbusRes.data.map((item) => ({ service: item.service, courier_id: item.provider_courier_id }));
          break;
        case "Xpressbees":
        case "XpressBees":
          // Keep Xpressbees' own courier id with each service: bookings send
          // it so Xpressbees ships exactly the service that was priced.
          const xpressRes = await axios.get(`${REACT_APP_BACKEND_URL}/Xpressbees/getCourierList`);
          services = xpressRes.data.map((item) => ({ service: item.service, courier_id: item.courier_id || item.provider_courier_id }));
          break;
        case "Shiprocket":
          const shipRes = await axios.get(`${REACT_APP_BACKEND_URL}/Shiprocket/getAllActiveCourierServices`, {
            params: accountName ? { account: accountName } : undefined,
          });
          services = shipRes.data; // Store full objects [{service, provider_courier_id}]
          break;
        case "ShipexIndia":
        case "ShipxIndia":
          const shipexRes = await axios.get(`${REACT_APP_BACKEND_URL}/ShipexIndia/getCourierServices`);
          services = shipexRes.data; // [{service}] — the exact courierServiceName that booking sends
          break;
        case "Dtdc":
          services = ["B2C SMART EXPRESS", "B2C PRIORITY", "B2C GROUND ECONOMY"];
          break;
        default:
          services = [];
          break;
      }
      setProviderServices(services);
    } catch (error) {
      console.error(`Error fetching ${providerName} services:`, error);
      setProviderServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  useEffect(() => {
    const fetchCouriers = async () => {
      try {
        const userInfo = getUserInfoFromToken();
        const isMainAdmin = userInfo?.type !== "employee" && (userInfo?.isAdmin || userInfo?.adminTab);

        if (isSidebarAdmin || isMainAdmin) {
          setEmployeeAccess({ canView: true, canAction: true, canUpdate: true });
          setShowEmployeeAuthModal(false);
        } else {
          const token = Cookies.get("session");
          const empRes = await axios.get(`${REACT_APP_BACKEND_URL}/staffRole/verify`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const employee = empRes.data.employee;
          const canView = !!employee?.accessRights?.courier?.["courier service"]?.view;
          const canAction = !!employee?.accessRights?.courier?.["courier service"]?.action;
          const canUpdate = !!employee?.accessRights?.courier?.["courier service"]?.update;
          setEmployeeAccess({ canView, canAction, canUpdate });
          if (!canView) {
            setShowEmployeeAuthModal(true);
            return;
          }
          setShowEmployeeAuthModal(false);
        }

        const response = await axios.get(`${REACT_APP_BACKEND_URL}/allCourier/couriers`);
        setCourierProviders(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        const userInfo = getUserInfoFromToken();
        const isMainAdmin = userInfo?.type !== "employee" && (userInfo?.isAdmin || userInfo?.adminTab);
        if (isMainAdmin) {
          setEmployeeAccess({ canView: true, canAction: true, canUpdate: true });
          setShowEmployeeAuthModal(false);
        } else {
          setCourierProviders([]);
          setShowEmployeeAuthModal(true);
        }
      }
    };

    fetchCouriers();    if (location.state?.courierToEdit) {
      const editCourier = location.state.courierToEdit;
      setFormData({
        _id: editCourier._id || null,
        id: editCourier.id || null,
        // a Shiprocket service is edited under its account (courierName), see the effect below
        provider: editCourier.provider || "",
        courier: editCourier.courier || "",
        courierType: editCourier.courierType || "",
        name: editCourier.name || "",
        status: editCourier.status || "",
        courier_id: editCourier.courier_id || "",
      });
      setSelectedProvider(editCourier.provider);
      fetchServicesForProvider(providerTypeOf(editCourier.provider), editCourier.courierName || editCourier.provider);
    }
  }, [location.state, isSidebarAdmin, refresh]);

  // Editing a Shiprocket service: once the accounts are loaded, show the account it belongs to as its provider.
  useEffect(() => {
    const editCourier = location.state?.courierToEdit;
    if (!editCourier || editCourier.provider !== "Shiprocket" || !editCourier.courierName) return;
    const account = courierProviders.find((c) => c.courierName === editCourier.courierName && c.courierProvider === "Shiprocket");
    if (!account) return;
    setFormData((prev) => (prev.provider === "Shiprocket" ? { ...prev, provider: account.courierName } : prev));
    setSelectedProvider((prev) => (prev === "Shiprocket" ? account.courierName : prev));
  }, [courierProviders, location.state]);

  // Editing a NimbusPost service saved without a courier id: fill it in from the list for its courier
  useEffect(() => {
    if (providerTypeOf(selectedProvider) !== "NimbusPost" || !formData.courier || formData.courier_id) return;
    const found = providerServices.find((s) => s.service === formData.courier);
    if (found?.courier_id) setFormData((prev) => ({ ...prev, courier_id: found.courier_id }));
  }, [providerServices, selectedProvider, formData.courier, formData.courier_id]);

  const canSave = isSidebarAdmin || employeeAccess.canAction || employeeAccess.canUpdate;
  const canView = isSidebarAdmin || employeeAccess.canView;
  const canUpdate = isSidebarAdmin || employeeAccess.canUpdate;

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "provider") {
      setSelectedProvider(value);
      setFormData((prev) => ({ ...prev, courier: "", courier_id: "" }));
      fetchServicesForProvider(providerTypeOf(value), value);
    }
    
    if (name === "courier" && (providerTypeOf(selectedProvider) === "Shiprocket" || /^xpress\s*bees$/i.test(providerTypeOf(selectedProvider)) || providerTypeOf(selectedProvider) === "NimbusPost")) {
      const selectedService = providerServices.find(s => s.service === value);
      if (selectedService) {
        setFormData(prev => ({ ...prev, courier_id: selectedService.courier_id }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formProvider = providerTypeOf(formData.provider);
    const isLosung = formProvider === "Losung360";
    const isBoxd = formProvider === "BoxdLogistics";
    // Jiffy and ShipMaxx services are keyed by a code typed in by the admin
    // (Jiffy courier code / ShipMaxx carrier id), not picked from a list.
    const isJiffy = formProvider === "Jiffy";
    const isShipMaxx = formProvider === "ShipMaxx";

    if (!formData.provider || !formData.name || !formData.status || !formData.courierType) {
      Notification("Please fill all required fields", "info");
      return;
    }

    if (isLosung && !formData.courier_id) {
      Notification("Please enter Courier ID", "info");
      return;
    }

    if (isBoxd && !formData.courier) {
      Notification("Please enter Courier Service ID", "info");
      return;
    }

    if (isJiffy && !formData.courier) {
      Notification("Please enter Courier Code", "info");
      return;
    }

    if (isShipMaxx && !formData.courier) {
      Notification("Please enter Courier Code", "info");
      return;
    }

    if (providerServices.length > 0 && !isLosung && !isBoxd && !isJiffy && !isShipMaxx && !formData.courier) {
      Notification("Please select a Courier", "info");
      return;
    }

    if (formProvider === "NimbusPost" && !formData.courier_id) {
      Notification("This NimbusPost courier has no courier ID. Please select the courier again.", "info");
      return;
    }

    try {
      setLoading(true);
      const dataToSubmit = { ...formData };
      if (isLosung) {
        dataToSubmit.courier = formData.courier_id;
      }

      if (formData._id) {
        await axios.put(`${REACT_APP_BACKEND_URL}/courierServices/couriers/${formData._id}`, dataToSubmit);
      } else {
        await axios.post(`${REACT_APP_BACKEND_URL}/courierServices/couriers`, dataToSubmit);
      }
      Notification("Courier saved successfully!", "success");
      setRefresh(prev => !prev);
      setFormData({
        _id: null,
        id: null,
        provider: "",
        courier: "",
        courierType: "",
        name: "",
        status: "",
        courier_id: "",
      });
      setProviderServices([]);
      setSelectedProvider("");
      // Clear location state and go back to the list
      navigate("/adminDashboard/setup/courierservices/add/b2c", { replace: true, state: {} });
    } catch (error) {
      if (error.response?.data?.error?.includes("E11000") || error.response?.data?.error?.includes("duplicate key")) {
        Notification("Courier service name already exists. Please use a unique name.", "error");
      } else {
        Notification("Error saving courier", "error");
      }
      console.error("Error saving courier:", error);
    } finally {
      setLoading(false);
    }
  };


  if (!canSave && showEmployeeAuthModal) {
    return (
      <EmployeeAuthModal
        employeeModalShow={showEmployeeAuthModal}
        employeeModalClose={() => {
          setShowEmployeeAuthModal(false);
          if (window.history.length > 1) {
            window.history.back();
          } else {
            navigate("/adminDashboard");
          }
        }}
      />
    );
  }

  return (
    canView && (
      <div className="max-w-full sm:px-2 mx-auto">
        <div className="bg-white px-3 py-2 rounded-lg shadow-sm border border-gray-100 mb-2">
          <div className="flex items-center gap-2 mb-3 border-b border-gray-50">
            {formData._id ? (
              <FaEdit className="text-brand-primary w-3.5 h-3.5" />
            ) : (
              <FaPlus className="text-brand-primary w-3.5 h-3.5" />
            )}

            <h2 className="text-[12px] md:text-[14px] text-gray-700 font-[600]">
              {formData._id ? "Edit Courier Service" : "Add New Courier Service"}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {/* Provider */}
              <CustomDropdown
                label="Provider"
                name="provider"
                value={formData.provider}
                onChange={handleChange}
                options={[...new Set(courierProviders.map((c) => c.courierName))]}
                placeholder="Select Provider"
              />

              {/* Courier / Service ID */}
              {servicesLoading ? (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] sm:text-[12px] font-[600] text-gray-700">Courier</label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[10px] sm:text-[12px] font-[600] text-gray-500 flex items-center gap-2">
                    <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-brand-primary rounded-full animate-spin" />
                    Loading services, please wait...
                  </div>
                </div>
              ) : (providerServices.length > 0 || ["BoxdLogistics", "Losung360", "Jiffy", "ShipMaxx"].includes(providerTypeOf(selectedProvider))) && (
                providerTypeOf(selectedProvider) === "BoxdLogistics" ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] sm:text-[12px] font-[600] text-gray-700">Courier Service ID</label>
                    <input
                      type="text"
                      name="courier"
                      placeholder="Enter Courier Service ID"
                      value={formData.courier}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[10px] sm:text-[12px] focus:outline-none focus:border-brand-primary transition-all font-[600] text-gray-700"
                    />
                  </div>
                ) : providerTypeOf(selectedProvider) === "Jiffy" ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] sm:text-[12px] font-[600] text-gray-700">Courier Code</label>
                    <input
                      type="text"
                      name="courier"
                      placeholder="e.g. DT03"
                      value={formData.courier}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[10px] sm:text-[12px] focus:outline-none focus:border-brand-primary transition-all font-[600] text-gray-700"
                    />
                  </div>
                ) : providerTypeOf(selectedProvider) === "ShipMaxx" ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] sm:text-[12px] font-[600] text-gray-700">Courier Code</label>
                    <input
                      type="text"
                      name="courier"
                      placeholder="e.g. 2"
                      value={formData.courier}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[10px] sm:text-[12px] focus:outline-none focus:border-brand-primary transition-all font-[600] text-gray-700"
                    />
                  </div>
                ) : providerTypeOf(selectedProvider) === "Losung360" ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] sm:text-[12px] font-[600] text-gray-700">Courier ID</label>
                    <input
                      type="text"
                      name="courier_id"
                      placeholder="Enter Courier ID"
                      value={formData.courier_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[10px] sm:text-[12px] focus:outline-none focus:border-brand-primary transition-all font-[600] text-gray-700"
                    />
                  </div>
                ) : (
                  <CustomDropdown
                    label={providerTypeOf(selectedProvider) === "Dtdc" ? "Service Type" : "Courier"}
                    name="courier"
                    value={formData.courier}
                    onChange={handleChange}
                    options={providerServices.map(s => typeof s === 'string' ? s : s.service)}
                    placeholder={selectedProvider ? `Select ${providerTypeOf(selectedProvider) === "Dtdc" ? "Service Type" : "Courier"}` : "Select Provider first"}
                  />
                )
              )}

              {/* Courier Type */}
              <CustomDropdown
                label="Courier Type"
                name="courierType"
                value={formData.courierType}
                onChange={handleChange}
                options={["Domestic (Surface)", "Domestic (Air)"]}
                placeholder="Select Type"
              />

              {/* Status */}
              <CustomDropdown
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                options={["Enable", "Disable"]}
                placeholder="Select Status"
              />

              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] sm:text-[12px] font-[600] text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[10px] sm:text-[12px] focus:outline-none focus:border-brand-primary transition-all font-[600] text-gray-700"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className={`bg-brand-primary font-[700] text-white py-2 px-3 text-[10px] sm:text-[12px] rounded-lg shadow-sm transition-all hover:bg-opacity-90 active:scale-95 ${(!canSave || loading) ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={!canSave || loading}
              >
                {loading ? "Processing..." : (formData._id ? "Update Courier Service" : "Save Courier Service")}
              </button>
            </div>
          </form>
        </div>

        <CourierServiceList refresh={refresh} canUpdate={canUpdate} />
      </div>
    )
  );
}
